// UNE BOUCLE SANS FIN NE DOIT PAS FIGER L'APERÇU VIVANT.
//
// Dans la console, le code tourne dans un worker qu'on tue au bout de 4 s.
// Dans une PAGE, impossible : le script vit dans le même fil que la page, et
// sur le site, souvent dans le même que Reps lui-même. Un `while (true) {}`
// figeait donc tout l'onglet.
//
// On réécrit la CONDITION de chaque boucle avant de l'exécuter :
//
//   while (x < 10)        ->  while (__repsTour(1) && (x < 10))
//   for (i = 0; i < n; i++) ->  for (i = 0; __repsTour(2) && (i < n); i++)
//   for (;;)              ->  for (;__repsTour(3);)
//   do { } while (ok)     ->  do { } while (__repsTour(4) && (ok))
//
// Le nombre passé à la garde est la LIGNE de la boucle dans le fichier : c'est
// elle que la console affiche quand la boucle est arrêtée.
//
// `__repsTour` (public/static/reps/js-page.js) lève une erreur quand la même
// boucle tourne sans reprendre son souffle depuis trop longtemps. La
// réécriture n'ajoute AUCUN retour à la ligne : les numéros de ligne des
// erreurs restent ceux du fichier de l'apprenant. `for…of` et `for…in`
// parcourent une collection finie : on n'y touche pas.
//
// Un vrai analyseur serait trop lourd pour le téléphone : on lit le code
// jeton par jeton, juste assez pour ne pas prendre pour un mot-clé ce qui est
// dans une chaîne, un commentaire, un gabarit ou une expression régulière.
// Un code que cette lecture ne comprend pas est rendu TEL QUEL : la page
// tourne alors sans filet, jamais avec un code abîmé.
//
// Miroir : reps-app/src/features/lesson/live-page.ts (protegerBoucles), parité
// testée sur des cas et sur tous les scripts du parcours.

const MOTS_AVANT_REGEX = new Set([
    "return", "typeof", "instanceof", "in", "of", "new", "delete", "void",
    "throw", "case", "do", "else", "yield", "await",
]);

// Fin de la chaîne (ou du gabarit) qui commence en `i` ; -1 si elle ne se
// ferme pas.
function finDeChaine(code, i) {
    const q = code[i];
    let j = i + 1;
    while (j < code.length) {
        const c = code[j];
        if (c === "\\") {
            j += 2;
            continue;
        }
        if (q === "`" && c === "$" && code[j + 1] === "{") {
            const k = finDeBloc(code, j + 1);
            if (k < 0) return -1;
            j = k + 1;
            continue;
        }
        if (c === q) return j;
        if (q !== "`" && c === "\n") return -1;
        j++;
    }
    return -1;
}

// Fin du bloc (accolade, parenthèse ou crochet) ouvert en `i`.
function finDeBloc(code, i) {
    const ouvre = code[i];
    const ferme = ouvre === "{" ? "}" : ouvre === "(" ? ")" : "]";
    let profondeur = 0;
    let j = i;
    let precedent = "";
    while (j < code.length) {
        const saut = sauter(code, j, precedent);
        if (saut !== null) {
            if (saut < 0) return -1;
            j = saut;
            precedent = "x";
            continue;
        }
        const c = code[j];
        if (c === ouvre) profondeur++;
        else if (c === ferme) {
            profondeur--;
            if (profondeur === 0) return j;
        }
        if (!/\s/.test(c)) precedent = c;
        j++;
    }
    return -1;
}

// Si `i` ouvre un commentaire, une chaîne, un gabarit ou une expression
// régulière, rend l'index juste après (ou -1 s'il ne se ferme pas). Sinon null.
// `precedent` : le dernier caractère significatif, qui dit si un `/` divise ou
// ouvre une expression régulière.
function sauter(code, i, precedent) {
    const c = code[i];
    if (c === "/" && code[i + 1] === "/") {
        const k = code.indexOf("\n", i);
        return k < 0 ? code.length : k;
    }
    if (c === "/" && code[i + 1] === "*") {
        const k = code.indexOf("*/", i + 2);
        return k < 0 ? -1 : k + 2;
    }
    if (c === '"' || c === "'" || c === "`") {
        const k = finDeChaine(code, i);
        return k < 0 ? -1 : k + 1;
    }
    if (c === "/" && (precedent === "" || /[(,=:[!&|?{};+\-*%<>~^]/.test(precedent) || precedent === "mot-avant-regex")) {
        let j = i + 1;
        let classe = false;
        while (j < code.length) {
            const d = code[j];
            if (d === "\\") {
                j += 2;
                continue;
            }
            if (d === "\n") return -1;
            if (d === "[") classe = true;
            else if (d === "]") classe = false;
            else if (d === "/" && !classe) break;
            j++;
        }
        if (j >= code.length) return -1;
        j++;
        while (j < code.length && /[a-z]/i.test(code[j])) j++;
        return j;
    }
    return null;
}

/** Le code avec la condition de chaque boucle gardée ; tel quel en cas de doute. */
export function protegerBoucles(code, fonction = "__repsTour") {
    const source = String(code == null ? "" : code);
    const insertions = []; // [index, texte] ou [index, texte, indexFin, texteFin]
    let n = 0;
    let i = 0;
    let precedent = "";
    let motPrecedent = "";
    let ok = true;
    while (i < source.length) {
        const saut = sauter(source, i, precedent === "" ? "" : MOTS_AVANT_REGEX.has(motPrecedent) ? "mot-avant-regex" : precedent);
        if (saut !== null) {
            if (saut < 0) {
                ok = false;
                break;
            }
            i = saut;
            precedent = "x";
            motPrecedent = "";
            continue;
        }
        const c = source[i];
        if (/[A-Za-z_$]/.test(c)) {
            let j = i;
            while (j < source.length && /[\w$]/.test(source[j])) j++;
            const mot = source.slice(i, j);
            const apresPoint = precedent === ".";
            if (!apresPoint && (mot === "while" || mot === "for")) {
                // La parenthèse qui suit, commentaires et espaces sautés.
                let k = j;
                while (k < source.length) {
                    if (/\s/.test(source[k])) {
                        k++;
                        continue;
                    }
                    const s = sauter(source, k, "x");
                    if (s !== null && (source[k] === "/")) {
                        if (s < 0) break;
                        k = s;
                        continue;
                    }
                    break;
                }
                // `for await (...)` : on laisse.
                if (source[k] === "(") {
                    const fin = finDeBloc(source, k);
                    if (fin < 0) {
                        ok = false;
                        break;
                    }
                    const dedans = source.slice(k + 1, fin);
                    n++;
                    const garde = `${fonction}(${ligneDe(source, i)})`;
                    if (mot === "while") {
                        insertions.push([k + 1, `${garde} && (`, fin, ")"]);
                    } else {
                        const pv = pointsVirgules(dedans);
                        if (pv && pv.length === 2) {
                            const a = k + 1 + pv[0] + 1;
                            const b = k + 1 + pv[1];
                            const cond = source.slice(a, b);
                            if (cond.trim() === "") insertions.push([a, garde]);
                            else insertions.push([a, `${garde} && (`, b, ")"]);
                        } else {
                            n--; // for…of, for…in : rien à garder
                        }
                    }
                }
            }
            precedent = "x";
            motPrecedent = mot;
            i = j;
            continue;
        }
        if (!/\s/.test(c)) {
            precedent = c;
            motPrecedent = "";
        }
        i++;
    }
    if (!ok || insertions.length === 0) return source;
    // Des fins après des débuts : on insère de la fin vers le début.
    const points = [];
    for (const ins of insertions) {
        points.push([ins[0], ins[1]]);
        if (ins.length > 2) points.push([ins[2], ins[3]]);
    }
    points.sort((x, y) => y[0] - x[0] || (y[1] === ")" ? -1 : 1));
    let out = source;
    for (const [index, texte] of points) out = out.slice(0, index) + texte + out.slice(index);
    return out;
}

// Numéro de ligne (à partir de 1) de la position `index`.
function ligneDe(code, index) {
    let l = 1;
    for (let k = 0; k < index; k++) if (code.charCodeAt(k) === 10) l++;
    return l;
}

// Positions des `;` de premier niveau dans l'en-tête d'un `for`.
function pointsVirgules(dedans) {
    const pv = [];
    let i = 0;
    let precedent = "";
    while (i < dedans.length) {
        const saut = sauter(dedans, i, precedent);
        if (saut !== null) {
            if (saut < 0) return null;
            i = saut;
            precedent = "x";
            continue;
        }
        const c = dedans[i];
        if (c === "(" || c === "[" || c === "{") {
            const f = finDeBloc(dedans, i);
            if (f < 0) return null;
            i = f + 1;
            precedent = "x";
            continue;
        }
        if (c === ";") pv.push(i);
        if (!/\s/.test(c)) precedent = c;
        i++;
    }
    return pv;
}
